import { Alert, AlertTitle, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { buildInfo } from '@/config/buildInfo';
import { useWydanie, skrotSha } from '@/lib/useWydanie';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { PasSekcji } from '@/components/PasSekcji';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { ErrorState, LoadingState } from '@/components/states';
import { formatCount, formatGrosze, formatRange } from '@/lib/format';
import { TIME_AXIS_HINT, TIME_AXIS_LABEL } from '@/lib/metricBasis';
import {
  CAPABILITIES,
  czyMoznaWygasicPanel,
  podsumujParytet,
  type Capability,
  type CapabilityClass,
  type ParityStatus,
} from '@/lib/parityRegistry';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useSchoolSales } from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * GOTOWOŚĆ / MIGRACJA — ile ze starego panelu już umiemy i czego jeszcze nie wolno wyłączyć
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ŹRÓDŁEM PRAWDY JEST `src/lib/parityRegistry.ts`. Ten ekran RENDERUJE rejestr — nie trzyma
 * własnej listy zdolności. Gdyby trzymał, produkt miałby dwie prawdy o tym samym i pierwsza
 * zmiana statusu rozjechałaby je po cichu; dokładnie tak powstał licznik „0/13", który liczył
 * jedną funkcję dwa razy.
 *
 * Tabele w tym pliku (etykiety statusów, etykiety klas, tony barwne) to WARSTWA PREZENTACJI:
 * mapują wartość wyliczeniową na słowo i kolor. Nie zawierają ani jednej zdolności, ani jednego
 * statusu przypisanego ręcznie do identyfikatora — wszystko, co dotyczy „która zdolność ma jaki
 * status", jest czytane z rejestru.
 *
 * ŚWIADOMIE BEZ JEDNEGO PROCENTU GOTOWOŚCI (ADR-004) — uzasadnienie stoi na ekranie, nie tylko
 * w komentarzu, bo to jest decyzja produktowa, którą odbiorca ma prawo zakwestionować.
 */

// ── Warstwa prezentacji: barwa ────────────────────────────────────────────────
// ZŁOTO jest kolorem marki i interakcji, NIGDY nie znaczy „ostrzeżenie" (tokens.css). Dlatego
// statusy dostają wyłącznie rodzinę semantyczną, a złoto pojawia się na tym ekranie jeden raz —
// jako akcent wyróżnienia przy O7, czyli tam, gdzie nie niesie żadnej oceny ryzyka.

type Ton = 'ok' | 'uwaga' | 'krytyczny' | 'neutralny';

const TON: Record<Ton, { tekst: string; tlo: string }> = {
  ok: { tekst: 'var(--sukces)', tlo: 'var(--sukces-tlo)' },
  uwaga: { tekst: 'var(--uwaga)', tlo: 'var(--uwaga-tlo)' },
  krytyczny: { tekst: 'var(--krytyczny)', tlo: 'var(--krytyczny-tlo)' },
  neutralny: { tekst: 'var(--neutralny)', tlo: 'var(--neutralny-tlo)' },
};

interface OpisStatusu {
  etykieta: string;
  /** Co ten status ZNACZY dla decyzji o wygaszeniu panelu — nie co znaczy jego nazwa. */
  sens: string;
  ton: Ton;
}

/**
 * Opis KAŻDEGO statusu dopuszczonego przez typ. Rekord jest wyczerpujący celowo: dopisanie
 * nowego statusu w rejestrze bez opisu tutaj nie skompiluje się, zamiast wyświetlić surową
 * stałą techniczną komuś, kto ma na jej podstawie podjąć decyzję.
 */
const OPIS_STATUSU: Record<ParityStatus, OpisStatusu> = {
  NO_LEGACY_EQUIVALENT: {
    etykieta: 'Brak odpowiednika',
    sens: 'Control Center nie ma zamiennika tej funkcji. Dopóki ktokolwiek jej używa, panel zostaje.',
    ton: 'krytyczny',
  },
  SEMANTIC_MISMATCH: {
    etykieta: 'Rozjazd nierozstrzygnięty',
    sens: 'Odpowiednik istnieje, ale liczy inaczej niż panel i nie zapadła decyzja, która definicja jest wiążąca.',
    ton: 'uwaga',
  },
  SEMANTIC_MISMATCH_BY_DESIGN: {
    etykieta: 'Rozjazd z projektu',
    sens: 'Liczby różnią się celowo, przyczyna jest nazwana w rejestrze. To udokumentowana decyzja, nie usterka do naprawienia.',
    ton: 'neutralny',
  },
  FIXED_TO_CANONICAL: {
    etykieta: 'Zamknięte regułą kanoniczną',
    sens: 'Rozstrzygnięte i wdrożone: Control Center liczy zgodnie z przyjętą regułą. Po tej stronie nie ma już pracy.',
    ton: 'ok',
  },
  VERIFIED_EQUAL: {
    etykieta: 'Liczby zgodne',
    sens: 'Porównanie z panelem wykonane i wyszło tak samo.',
    ton: 'ok',
  },
  VERIFIED_SEMANTICALLY_EQUIVALENT: {
    etykieta: 'Zgodne co do znaczenia',
    sens: 'Definicje uznano za równoważne, choć liczby nie są identyczne co do grosza.',
    ton: 'ok',
  },
  BLOCKED_BY_DATA: {
    etykieta: 'Zablokowane danymi',
    sens: 'Odpowiednika nie da się dziś sprawdzić, bo brakuje danych po którejś ze stron.',
    ton: 'uwaga',
  },
  NOT_TESTED: {
    etykieta: 'Niesprawdzone',
    sens: 'Nikt jeszcze nie porównał. To brak wiedzy, nie wynik.',
    ton: 'neutralny',
  },
};

/** Kolejność czytania: najpierw to, co blokuje, na końcu to, co domknięte. */
const KOLEJNOSC_STATUSOW: readonly ParityStatus[] = [
  'NO_LEGACY_EQUIVALENT',
  'SEMANTIC_MISMATCH',
  'BLOCKED_BY_DATA',
  'NOT_TESTED',
  'SEMANTIC_MISMATCH_BY_DESIGN',
  'FIXED_TO_CANONICAL',
  'VERIFIED_SEMANTICALLY_EQUIVALENT',
  'VERIFIED_EQUAL',
];

const OPIS_KLASY: Record<CapabilityClass, { etykieta: string; sens: string }> = {
  ANALYTICS: {
    etykieta: 'Analityka i rejestry (rola ADMIN)',
    sens: 'To jest zakres R5-15. Za braki w tej klasie Control Center odpowiada.',
  },
  OPERATIONAL: {
    etykieta: 'Operacje (rola DELIVER)',
    sens:
      'Poza zakresem R5-15. Control Center nie dostaje za te pozycje oceny negatywnej — ale ich brak ' +
      'trzyma panel włączony dla osób, które tę pracę wykonują.',
  },
};

function numerZdolnosci(id: string): number {
  const n = Number.parseInt(id.replace(/\D+/g, ''), 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

function ZnacznikStatusu({ status }: { status: ParityStatus }) {
  const opis = OPIS_STATUSU[status];
  return (
    <Chip
      size="small"
      label={opis.etykieta}
      title={opis.sens}
      sx={{
        background: TON[opis.ton].tlo,
        color: TON[opis.ton].tekst,
        fontWeight: 700,
        borderRadius: 'var(--r-pigulka)',
      }}
    />
  );
}

/** Wiersz zdolności. Wszystko, co pokazuje, pochodzi z rejestru — łącznie z brakiem przyczyny. */
function WierszZdolnosci({ zdolnosc }: { zdolnosc: Capability }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'grid',
        gap: 1,
        gridTemplateColumns: { xs: '1fr', md: '72px 1fr auto' },
        alignItems: 'start',
      }}
    >
      <Typography
        className="sygnal-liczba"
        sx={{ fontFamily: 'var(--krój-mono)', fontWeight: 700, color: 'var(--tekst-slaby)' }}
      >
        {zdolnosc.id}
      </Typography>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }}>{zdolnosc.name}</Typography>
        <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mt: 0.5 }}>
          {/* Brak przyczyny NIE jest dopisywany domysłem — zmyślone uzasadnienie brzmi
              wiarygodnie i przez to jest gorsze niż jego jawny brak. */}
          {zdolnosc.uwaga ?? 'Rejestr nie podaje przyczyny dla tej pozycji.'}
        </Typography>
        {zdolnosc.aliasWycofany ? (
          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 0.5 }}>
            Identyfikator wycofany: {zdolnosc.aliasWycofany} — opisywał tę samą zdolność, dlatego nie jest
            liczony osobno.
          </Typography>
        ) : null}
      </Box>

      <Stack spacing={1} sx={{ alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
        <ZnacznikStatusu status={zdolnosc.status} />
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)' }}>
          {zdolnosc.maOdpowiednikWCC ? 'odpowiednik w Control Center: jest' : 'odpowiednik w Control Center: brak'}
        </Typography>
      </Stack>
    </Paper>
  );
}

function Sekcja({
  tytul,
  wstep,
  children,
}: {
  tytul: string;
  wstep?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: wstep ? 0.5 : 2 }}>
        {tytul}
      </Typography>
      {wstep ? (
        <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mb: 2, maxWidth: '78ch' }}>
          {wstep}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

export function GotowoscPage() {
  const wydanieCC = useWydanie();
  const shaWydania = skrotSha(wydanieCC.data?.sha);
  const [range] = useDateRange();
  const windowLabel = formatRange(range.date_from, range.date_to);

  // Jedyny odczyt API na tym ekranie. Rejestr twierdzi, że ZERO zdolności ma potwierdzoną
  // równość liczbową; ten odczyt sprawdza założenie, na którym to twierdzenie stoi — czy
  // w wybranym oknie po naszej stronie w ogóle jest co porównywać.
  const sprzedaz = useSchoolSales();

  const podsumowanie = podsumujParytet();
  const wszystkie = [...CAPABILITIES].sort((a, b) => numerZdolnosci(a.id) - numerZdolnosci(b.id));

  const bezOdpowiednika = wszystkie
    .filter((c) => !c.maOdpowiednikWCC)
    // Analityka najpierw: to ona rozstrzyga pytanie A, czyli decyzję, którą admin podejmuje sam.
    .sort((a, b) => (a.klasa === b.klasa ? 0 : a.klasa === 'ANALYTICS' ? -1 : 1));

  const zOdpowiednikiem = wszystkie.filter((c) => c.maOdpowiednikWCC);
  const analityczne = wszystkie.filter((c) => c.klasa === 'ANALYTICS');
  const analitykaBezOdpowiednika = analityczne.filter((c) => !c.maOdpowiednikWCC);
  const operacyjneBezOdpowiednika = wszystkie.filter((c) => c.klasa === 'OPERATIONAL' && !c.maOdpowiednikWCC);

  // ODPOWIEDŹ A liczona z rejestru, nie wpisana. „CZĘŚCIOWO" jest tu pełnoprawną odpowiedzią:
  // zaokrąglenie jej do TAK albo NIE zmusiłoby do zmyślenia rozstrzygnięcia, którego nie ma.
  const odpowiedzA: 'TAK' | 'NIE' | 'CZĘŚCIOWO' =
    analitykaBezOdpowiednika.length === 0
      ? 'TAK'
      : analitykaBezOdpowiednika.length === analityczne.length
        ? 'NIE'
        : 'CZĘŚCIOWO';

  const odpowiedzB: 'TAK' | 'NIE' = czyMoznaWygasicPanel() ? 'TAK' : 'NIE';

  // Statusy WYSTĘPUJĄCE w rejestrze — wyliczone, nie wypisane. Status z zerem pozycji nie jest
  // pokazywany: sugerowałby, że coś zmierzono i wyszło zero, a to nie to samo co „nie ma takich".
  const obecneStatusy = [...new Set(wszystkie.map((c) => c.status))].sort((a, b) => {
    const wa = KOLEJNOSC_STATUSOW.indexOf(a);
    const wb = KOLEJNOSC_STATUSOW.indexOf(b);
    return (wa === -1 ? 99 : wa) - (wb === -1 ? 99 : wb);
  });

  const obecneKlasy = [...new Set(wszystkie.map((c) => c.klasa))];
  const o7 = wszystkie.find((c) => c.id === 'O7');

  // ── Odczyt na żywo: zero czy pusto ──────────────────────────────────────────
  const liczbaZamowien = sprzedaz.data?.summary.total_orders ?? null;
  const liczbaSzkol = sprzedaz.data?.schools.length ?? null;
  const ziarno = { wierszy: liczbaZamowien, bytow: liczbaSzkol };
  const zrodloPuste = czyPustyZbior(ziarno);
  const wartoscSprzedazy = wartoscDoPokazania(sprzedaz.data?.summary.total_sales_grosze, ziarno);

  const stempelArtefaktu = `${(shaWydania ?? 'niedostępny bez sesji')} · zbudowany ${buildInfo.buildTime}`;
  const swiezoscRejestru =
    `rejestr pochodzi z artefaktu ${stempelArtefaktu}; data samego przeglądu panelu NIE jest ` +
    'w rejestrze zapisana, więc wiek tej wiedzy pozostaje nieustalony';
  const ograniczenieRejestru =
    'Rejestr jest spisem prowadzonym ręcznie i nie jest automatycznie uzgadniany z tablicą tras ' +
    'ani z listą endpointów Control Center — pozycja może opisywać stan sprzed zmian w kodzie.';

  const dowodBlokerow: Dowod = {
    tytul: 'Zdolności bez odpowiednika w Control Center',
    etykietaWartosci: 'Pozycje rejestru bez zamiennika',
    wartosc: String(bezOdpowiednika.length),
    // Bez `podstawa`: to jest licznik pozycji rejestru, a nie metryka biznesowa. Doklejenie tu
    // osi czasu albo jednostki wolumenu sugerowałoby pomiar, którego nie ma.
    zrodlo: 'Rejestr zdolności w kodzie: src/lib/parityRegistry.ts',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: swiezoscRejestru,
    wyliczenie: 'Liczba pozycji rejestru, dla których maOdpowiednikWCC = false. Jedna zdolność = jeden wiersz, bez ważenia.',
    szczegoly: [
      ['Unikalnych zdolności', formatCount(podsumowanie.unikalnychZdolnosci)],
      ['Analitycznych (rola ADMIN)', formatCount(podsumowanie.analityczne)],
      ['Operacyjnych (rola DELIVER)', formatCount(podsumowanie.operacyjne)],
      ['W tym bez odpowiednika: analityka', formatCount(analitykaBezOdpowiednika.length)],
      ['W tym bez odpowiednika: operacje', formatCount(operacyjneBezOdpowiednika.length)],
      ['Identyfikatory wycofane', podsumowanie.aliasyWycofane.join(', ') || 'brak'],
    ],
    proba: `${formatCount(podsumowanie.unikalnychZdolnosci)} pozycji rejestru`,
    ograniczenia: [
      ograniczenieRejestru,
      'Zdolności nie są ważone pracochłonnością ani częstością użycia — pozycja używana codziennie ' +
        'liczy się tyle samo, co używana raz na kwartał.',
      'Mianownik to 12, a nie 13: identyfikator O13 opisywał tę samą zdolność co O5 i jest trzymany ' +
        'jako alias, żeby wcześniejsze raporty dały się z tym zestawić.',
    ],
    czegoNieWynika:
      'Z tej liczby nie wynika, ile pracy zostało do wykonania — rejestr liczy zdolności, nie godziny, ' +
      'a brak rejestru transakcji i brak wydruku etykiet ważą tu tyle samo, choć są zupełnie różnymi ' +
      'przedsięwzięciami. Nie wynika z niej również, że pozostałe zdolności działają poprawnie: ' +
      '„ma odpowiednik" mówi wyłącznie, że ekran istnieje, a nie że pokazuje te same liczby co panel.',
  };

  const dowodPokrycia: Dowod = {
    tytul: 'Pokrycie analityki roli ADMIN',
    etykietaWartosci: 'Zdolności analityczne z odpowiednikiem',
    wartosc: `${formatCount(podsumowanie.maOdpowiednik)} z ${formatCount(podsumowanie.analityczne)}`,
    zrodlo: 'Rejestr zdolności w kodzie: src/lib/parityRegistry.ts',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: swiezoscRejestru,
    wyliczenie:
      'Pozycje klasy ANALYTICS z maOdpowiednikWCC = true, podzielone przez wszystkie pozycje tej klasy. ' +
      'Operacje (rola DELIVER) są poza mianownikiem, bo są poza zakresem R5-15.',
    szczegoly: [
      ['Zdolności analityczne', formatCount(podsumowanie.analityczne)],
      ['Z odpowiednikiem', formatCount(podsumowanie.maOdpowiednik)],
      ['Zweryfikowanych liczbowo', formatCount(podsumowanie.zweryfikowaneLiczbowo)],
      ['Rozjazd nierozstrzygnięty', formatCount(podsumowanie.semanticMismatch)],
      ['Rozjazd z projektu', formatCount(podsumowanie.mismatchZProjektu)],
      ['Zamknięte regułą kanoniczną', formatCount(podsumowanie.zamknieteKanonicznie)],
    ],
    proba: `${formatCount(podsumowanie.analityczne)} zdolności analitycznych`,
    ograniczenia: [
      ograniczenieRejestru,
      'To są DWA różne pomiary w jednym miejscu: „ma odpowiednik" i „liczby zweryfikowane". ' +
        'Pierwszy wynosi dziś ' +
        formatCount(podsumowanie.maOdpowiednik) +
        ', drugi ' +
        formatCount(podsumowanie.zweryfikowaneLiczbowo) +
        ' — dlatego nie da się ich uczciwie zapisać jednym procentem.',
      'Rejestr nie zapisuje daty ani wyniku próby weryfikacji liczbowej, więc nie da się z niego ' +
        'ustalić, czy porównanie z panelem kiedykolwiek podjęto.',
    ],
    czegoNieWynika:
      'Nie wynika z tego, że po zbudowaniu brakujących zdolności analitycznych panel dałoby się wyłączyć — ' +
      'zostają zdolności operacyjne, które rejestr trzyma w osobnej klasie. Nie wynika też, że te ' +
      'zdolności zgadzają się liczbowo z panelem: pozycji zweryfikowanych liczbowo jest ' +
      `${formatCount(podsumowanie.zweryfikowaneLiczbowo)}, a ta liczba mierzy wyłącznie istnienie odpowiednika.`,
  };

  const dowodZrodla: Dowod = {
    tytul: 'Materiał do porównania z panelem w wybranym oknie',
    etykietaWartosci: 'Wartość wg cennika',
    wartosc: wartoscSprzedazy === null ? null : formatGrosze(wartoscSprzedazy),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales → crm_order_items, szkoła z transakcji',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: windowLabel,
    wyliczenie: 'Σ (cena katalogowa pozycji × ilość) po pozycjach zamówień w oknie',
    szczegoly: [
      ['Zamówienia w oknie', formatCount(liczbaZamowien)],
      ['Szkoły w wyniku', formatCount(liczbaSzkol)],
      ['Przypisanie do szkoły', 'szkoła z TRANSAKCJI (stan z dnia zakupu)'],
    ],
    proba: `${formatCount(liczbaZamowien)} zamówień`,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero pozycji zamówień i zero szkół w tym oknie. To NIE jest ' +
              'zmierzone zero — nie ma czego sumować.',
          ]
        : []),
      'Kwota faktycznie pobrana nie jest tu sumowana — dla części transakcji nie istnieje w źródle.',
      'Świeżość źródła nieustalona: liczby są poprawne dla momentu odczytu, ale ten moment jest nieznany.',
    ],
    czegoNieWynika:
      'To NIE jest przychód — wartość wg cennika liczy ceny przed zniżką szkolną. Nie wynika z niej ' +
      'także stan parytetu: ta liczba mówi wyłącznie, czy w wybranym oknie po stronie Control Center ' +
      'jest cokolwiek do porównania z panelem, i nic nie mówi o tym, czy takie porównanie wypadłoby zgodnie.',
  };

  return (
    <Box>
      <NaglowekEkranu
        tytul="Gotowość i migracja"
        opis={
          <>
            Ekran odpowiada na pytanie: czego ze starego panelu Control Center jeszcze nie umie i co z tego
            wynika dla dwóch RÓŻNYCH decyzji — mojej własnej oraz wyłączenia panelu dla wszystkich. Wszystkie
            pozycje pochodzą z rejestru zdolności w kodzie (<code>src/lib/parityRegistry.ts</code>); ten ekran
            rejestru nie kopiuje, tylko go renderuje.
          </>
        }
        akcja={<DateRangeFilter wariant="naglowek" />}
      />

      <Stack spacing={3} className="sygnal-kaskada">
        <PlytaGlowna
          etykieta="Zdolności bez odpowiednika w Control Center"
          wartosc={String(bezOdpowiednika.length)}
          opis={
            <Typography variant="body2">
              na {formatCount(podsumowanie.unikalnychZdolnosci)} unikalnych zdolności spisanych w rejestrze —
              to liczba, która trzyma stary panel przy życiu, a nie miara postępu
            </Typography>
          }
          zrodlo="Rejestr zdolności w kodzie: src/lib/parityRegistry.ts"
          okno={`stan rejestru w artefakcie ${stempelArtefaktu}`}
          dowod={dowodBlokerow}
          boczne={
            <>
              <MiniMetryka wartosc={formatCount(podsumowanie.unikalnychZdolnosci)} etykieta="unikalnych zdolności" />
              <MiniMetryka wartosc={formatCount(zOdpowiednikiem.length)} etykieta="z odpowiednikiem" />
              <MiniMetryka
                wartosc={formatCount(podsumowanie.zweryfikowaneLiczbowo)}
                etykieta="zweryfikowanych liczbowo"
              />
              <MiniMetryka wartosc={formatCount(podsumowanie.operacyjne)} etykieta="operacji poza zakresem" />
            </>
          }
        />

        {/* ── Dlaczego nie ma jednego procentu ─────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 3, borderStyle: 'dashed' }}>
          <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
            Decyzja produktowa
          </Typography>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Dlaczego nie pokazujemy jednego procentu gotowości
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--tekst-2)', maxWidth: '78ch' }}>
            Jeden wskaźnik musiałby zmieszać trzy różne pytania: czy Control Center MA odpowiednik danej
            funkcji, czy jego LICZBY zgadzają się z panelem i czy wolno panel WYGASIĆ. Odpowiedzi na te
            pytania są dziś różne, więc wskaźnik, który je uśrednia, nie odpowiada na żadne z nich.
            Poprzedni licznik brzmiał „0/13" i mówił, że nie działa nic — przy{' '}
            {formatCount(podsumowanie.maOdpowiednik)} zdolnościach analitycznych, które mają działający
            odpowiednik — a przy okazji liczył jedną funkcję dwa razy. Dlatego zamiast jednej liczby ten
            ekran pokazuje wymiary osobno i nazywa każdy z nich.
          </Typography>
        </Paper>

        {/*
          DZIAŁY Z MAKIETY. Ten ekran nie miał ANI JEDNEGO — a jest najdłuższy w produkcie
          i prowadzi przez cztery różne rodzaje treści: dwie bramki decyzyjne, rozstrzygnięte
          rozjazdy i rejestr. Bez nazwanych działów czyta się jak jeden ciąg paneli, w którym
          nie widać, gdzie kończy się decyzja, a zaczyna dokumentacja.
        */}
        <PasSekcji wariant="cichy" tytul="Dwie bramki, nie jedna" />

        {/* ── Dwa różne pytania ────────────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'stretch' }}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }} data-testid="gotowosc-pytanie-a">
            <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
              Pytanie A · decyzja osobista
            </Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Czy jako ADMIN mogę przestać używać starego panelu?
            </Typography>

            <Typography
              className="sygnal-liczba"
              sx={{
                fontFamily: 'var(--krój-display)',
                fontSize: 'clamp(32px, 4vw, 48px)',
                lineHeight: 1,
                color: odpowiedzA === 'TAK' ? 'var(--sukces)' : odpowiedzA === 'NIE' ? 'var(--krytyczny)' : 'var(--uwaga)',
                mb: 2,
              }}
            >
              {odpowiedzA}
            </Typography>

            <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
              {formatCount(podsumowanie.maOdpowiednik)} z {formatCount(podsumowanie.analityczne)} zdolności
              analitycznych ma w Control Center działający odpowiednik.{' '}
              {analitykaBezOdpowiednika.length > 0
                ? 'Pozostałe wymagają panelu — dopóki którejkolwiek z nich potrzebujesz, panel zostaje otwarty:'
                : 'Żadna zdolność analityczna nie wymaga już panelu.'}
            </Typography>

            {analitykaBezOdpowiednika.length > 0 ? (
              <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 1, pl: 3 }}>
                {analitykaBezOdpowiednika.map((c) => (
                  <Typography key={c.id} component="li" variant="body2" sx={{ color: 'var(--tekst-2)' }}>
                    <strong>{c.id}</strong> — {c.name}
                  </Typography>
                ))}
              </Stack>
            ) : null}

            <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mt: 2 }}>
              Odpowiednik funkcjonalny to nie to samo co zgodne liczby: pozycji z potwierdzoną równością
              liczbową jest {formatCount(podsumowanie.zweryfikowaneLiczbowo)}.
            </Typography>

            <Box sx={{ ml: -1, mt: 1 }}>
              <PrzyciskDowodu dowod={dowodPokrycia} etykieta="Skąd ta odpowiedź" />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, height: '100%' }} data-testid="gotowosc-pytanie-b">
            <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
              Pytanie B · decyzja o wyłączeniu dla wszystkich
            </Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Czy można wyłączyć stary panel globalnie?
            </Typography>

            <Typography
              className="sygnal-liczba"
              sx={{
                fontFamily: 'var(--krój-display)',
                fontSize: 'clamp(32px, 4vw, 48px)',
                lineHeight: 1,
                color: odpowiedzB === 'TAK' ? 'var(--sukces)' : 'var(--krytyczny)',
                mb: 2,
              }}
            >
              {odpowiedzB}
            </Typography>

            <Typography variant="body2" sx={{ color: 'var(--tekst-2)' }}>
              To nie jest porażka programu, tylko jego świadomie postawiona granica. Rejestr trzyma{' '}
              {formatCount(podsumowanie.operacyjne)} zdolności w klasie operacyjnej (rola DELIVER) i R5-15
              ich nie buduje — zakres tego programu to analityka i rejestry roli ADMIN. Dopóki ktokolwiek
              wykonuje w panelu tę pracę, panel działa dalej, niezależnie od tego, jak daleko zajdzie
              analityka:
            </Typography>

            {operacyjneBezOdpowiednika.length > 0 ? (
              <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 1, pl: 3 }}>
                {operacyjneBezOdpowiednika.map((c) => (
                  <Typography key={c.id} component="li" variant="body2" sx={{ color: 'var(--tekst-2)' }}>
                    <strong>{c.id}</strong> — {c.name}
                  </Typography>
                ))}
              </Stack>
            ) : null}

            <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mt: 2 }}>
              Odpowiedź liczy funkcja <code>czyMoznaWygasicPanel()</code> z rejestru: wymaga, żeby KAŻDA
              zdolność miała odpowiednik — także operacyjna. Gotowa analityka sama z siebie tej odpowiedzi
              nie zmienia.
            </Typography>
          </Paper>
        </Box>

        {/* ── Blokery ──────────────────────────────────────────────────────────────────────── */}
        <Sekcja
          tytul="Blokery wygaszenia panelu"
          wstep={
            'Wszystkie, nie trzy najważniejsze. Na ekranie, którego zadaniem jest kompletna lista blokad, ' +
            'skrócenie listy do trzech pozycji byłoby ukryciem pozostałych. Przyczyna przy każdej pozycji ' +
            'pochodzi z rejestru — tam, gdzie rejestr jej nie podaje, jest to napisane wprost.'
          }
        >
          <Stack spacing={1.5}>
            {bezOdpowiednika.map((c) => (
              <Box key={c.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
                <Box
                  sx={{
                    width: 4,
                    borderRadius: 'var(--r-drobny)',
                    background: c.klasa === 'ANALYTICS' ? 'var(--krytyczny)' : 'var(--uwaga)',
                    flex: 'none',
                  }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <WierszZdolnosci zdolnosc={c} />
                </Box>
              </Box>
            ))}
          </Stack>
          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 1.5 }}>
            Pasek po lewej rozdziela klasy: czerwony — analityka roli ADMIN (blokuje pytanie A i B),
            pomarańczowy — operacje roli DELIVER (blokuje wyłącznie pytanie B).
          </Typography>
        </Sekcja>

        {/* ── Odczyt na żywo: czy jest co porównywać ───────────────────────────────────────── */}
        <Sekcja
          tytul="Czy jest dziś co porównywać"
          wstep={
            'Porównanie liczb z panelem wymaga, żeby po obu stronach było co porównać. Ta sekcja mówi ' +
            'wyłącznie o naszej stronie i o wybranym oknie — nie o wyniku porównania.'
          }
        >
          {sprzedaz.isLoading ? <LoadingState label="Sprawdzam źródło sprzedaży…" /> : null}

          {sprzedaz.error ? (
            <ErrorState
              error={sprzedaz.error}
              context={
                'Błąd odczytu to NIE jest pusty zbiór. Dopóki CRM nie odpowie, nie wiadomo, czy w tym oknie ' +
                'jest materiał do porównania — i tego stanu nie zastępujemy zerem.'
              }
            />
          ) : null}

          {!sprzedaz.isLoading && !sprzedaz.error ? (
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'baseline', mb: 1 }} useFlexGap>
                <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                  Wartość wg cennika · okno {windowLabel}
                </Typography>
              </Stack>

              <Typography
                className="sygnal-liczba"
                data-cc-card-id="READINESS-12"
                sx={{ fontFamily: 'var(--krój-display)', fontSize: 'clamp(28px, 3vw, 40px)', lineHeight: 1, mb: 1 }}
              >
                {/* Brak ≠ zero. Pusty zbiór renderujemy jako „brak danych", nigdy jako 0,00 zł. */}
                {wartoscSprzedazy === null ? (
                  <Box component="span" sx={{ fontSize: '0.5em', color: 'var(--tekst-slaby)' }}>
                    brak danych
                  </Box>
                ) : (
                  formatGrosze(wartoscSprzedazy)
                )}
              </Typography>

              <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' }} />

              <Box sx={{ ml: -1, mt: 1 }}>
                <PrzyciskDowodu dowod={dowodZrodla} kartaId="READINESS-13" />
              </Box>

              {zrodloPuste ? (
                <Alert severity="warning" icon={false} sx={{ mt: 2, borderRadius: 'var(--r-maly)' }}>
                  <AlertTitle>Źródło sprzedaży zwróciło pusty zbiór</AlertTitle>
                  <Typography variant="body2">
                    Zero pozycji zamówień i zero szkół w oknie {windowLabel}. To nie jest zmierzone zero — nie
                    ma czego sumować, więc nie ma też czego zestawić z panelem. Rejestr nie zapisuje daty ani
                    wyniku próby weryfikacji liczbowej, więc z tego ekranu nie wynika, czy porównanie
                    kiedykolwiek podjęto.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 'var(--r-maly)' }}>
                  <Typography variant="body2">
                    W tym oknie jest materiał do porównania: {formatCount(liczbaZamowien)} zamówień w{' '}
                    {formatCount(liczbaSzkol)} szkołach. Rejestr wykazuje nadal{' '}
                    {formatCount(podsumowanie.zweryfikowaneLiczbowo)} zdolności zweryfikowanych liczbowo —
                    dostępność materiału nie jest weryfikacją.
                  </Typography>
                </Alert>
              )}
            </Paper>
          ) : null}
        </Sekcja>

        {o7 ? <PasSekcji wariant="cichy" tytul="Rozjazdy z projektu — rozstrzygnięte, nie do naprawy" /> : null}

        {/* ── O7: rozstrzygnięcie, nie dług ────────────────────────────────────────────────── */}
        {o7 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              // Złoto = marka i wyróżnienie, NIGDY ostrzeżenie (tokens.css). Ten akcent mówi
              // „przeczytaj to", a nie „coś jest nie tak".
              borderLeft: '4px solid var(--zloto)',
              background: 'var(--powierzchnia-2)',
            }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }} useFlexGap>
              <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                {o7.id} · sprawa zamknięta
              </Typography>
              <ZnacznikStatusu status={o7.status} />
            </Stack>

            <Typography variant="h5" sx={{ mb: 1 }}>
              {o7.name} — zamknięte regułą kanoniczną
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }} useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                label={`wyniki biznesowe — ${TIME_AXIS_LABEL.DELIVERY_DATE}`}
                title={TIME_AXIS_HINT.DELIVERY_DATE}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`zachowanie zakupowe — ${TIME_AXIS_LABEL.ORDER_DATE}`}
                title={TIME_AXIS_HINT.ORDER_DATE}
              />
            </Stack>

            <Typography variant="body2" sx={{ color: 'var(--tekst-2)', maxWidth: '78ch' }}>
              {o7.uwaga ?? 'Rejestr nie podaje przyczyny dla tej pozycji.'}
            </Typography>

            <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mt: 2, maxWidth: '78ch' }}>
              Ta pozycja nie należy do długu do spłacenia i nie należy jej czytać jako otwartego rozjazdu.
              Reguła jest utrwalona w kontrakcie metryk (<code>lib/metricBasis.ts</code>): typ osi czasu nie
              dopuszcza innej wartości, a każdy ekran podaje oś przy liczbie.
            </Typography>
          </Paper>
        ) : null}

        {/* ── Statusy obecne w rejestrze ───────────────────────────────────────────────────── */}
        <Sekcja
          tytul="Statusy obecne w rejestrze"
          wstep={
            'Każdy status osobno, z liczbą pozycji i z tym, co znaczy dla decyzji. Statusy dopuszczone ' +
            'przez rejestr, ale niewystępujące w nim, nie są pokazywane — pozycja z zerem sugerowałaby, ' +
            'że coś zmierzono i wyszło zero.'
          }
        >
          <Stack spacing={1.5}>
            {obecneStatusy.map((status) => {
              const opis = OPIS_STATUSU[status];
              const pozycje = wszystkie.filter((c) => c.status === status);
              return (
                <Paper key={status} variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
                    useFlexGap
                  >
                    <ZnacznikStatusu status={status} />
                    <Typography
                      className="sygnal-liczba"
                      sx={{ fontFamily: 'var(--krój-display)', fontSize: 24, lineHeight: 1 }}
                    >
                      {formatCount(pozycje.length)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', fontFamily: 'var(--krój-mono)' }}>
                      {status}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'var(--tekst-2)', maxWidth: '78ch' }}>
                    {opis.sens}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 1 }}>
                    Pozycje: {pozycje.map((c) => `${c.id} ${c.name}`).join(' · ')}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </Sekcja>

        <PasSekcji wariant="cichy" tytul="Rejestr parytetu" />

        {/* ── Grupy wg kategorii ───────────────────────────────────────────────────────────── */}
        <Sekcja
          tytul="Zdolności wg kategorii"
          wstep={
            'Kategoria decyduje o tym, kto odpowiada za brak. Rozdzielenie klas jest powodem, dla którego ' +
            'jedna liczba gotowości byłaby myląca: te dwie grupy należą do dwóch różnych programów.'
          }
        >
          <Stack spacing={3}>
            {obecneKlasy.map((klasa) => {
              const pozycje = wszystkie.filter((c) => c.klasa === klasa);
              const opis = OPIS_KLASY[klasa];
              return (
                <Box key={klasa}>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }} useFlexGap>
                    <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                      {opis.etykieta}
                    </Typography>
                    <Typography variant="caption" className="sygnal-liczba" sx={{ color: 'var(--tekst-slaby)' }}>
                      {formatCount(pozycje.filter((c) => c.maOdpowiednikWCC).length)} z{' '}
                      {formatCount(pozycje.length)} ma odpowiednik
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 1.5, maxWidth: '78ch' }}>
                    {opis.sens}
                  </Typography>
                  <Stack spacing={1}>
                    {pozycje.map((c) => (
                      <WierszZdolnosci key={c.id} zdolnosc={c} />
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mt: 2, maxWidth: '78ch' }}>
            Mianownik to {formatCount(podsumowanie.unikalnychZdolnosci)} unikalnych zdolności. Identyfikatory
            wycofane ({podsumowanie.aliasyWycofane.join(', ') || 'brak'}) opisywały te same funkcje co pozycje
            wciąż obecne i celowo nie zostały skasowane — bez nich wcześniejsze raporty przestałyby się
            zgadzać i nikt nie wiedziałby dlaczego.
          </Typography>
        </Sekcja>
      </Stack>
    </Box>
  );
}
